import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import _ from 'lodash';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { Button } from '../../../../../common/button';
import useNotification from '../../../../../../core/hooks/use_notification';
import { getFoodPartnerOrderHistoryNotificationData } from '../../../../../../core/utils/notification_utils';
import PaginationControls from '../../../../../common/pagination_controls';
import { getDefaultFoodPartnerOrderHistoryPaginationQueryValues } from '../../../../../../core/utils/api_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../../core/constants';
import { getFoodPartnerOrderHistoryAction, getFoodPartnerOrderHistoryState, invalidateFoodPartnerOrderHistory } from '../../../../../../features/inventory/get_food_partner_order_history';
import FoodPartnerOrderHistory from './food_partner_order_history';
import FoodPartnerOrderHistoryDetails from './food_partner_order_history_details';

const initialQueryParams = {
    ...getDefaultFoodPartnerOrderHistoryPaginationQueryValues,
    fromDate: '',
    toDate: ''
};

const CopyHistoricalDialog = ({ form, onCopyHistoricalOrder }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getFoodPartnerOrderHistory = useSelector(getFoodPartnerOrderHistoryState);

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(0);
    const [orderIndex, setOrderIndex] = useState(0);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    useEffect(() => {
        if (open) {
            const fromDate = queryParams?.fromDate && queryParams?.fromDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);
            const toDate = queryParams?.toDate && queryParams?.toDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);

            dispatch(getFoodPartnerOrderHistoryAction({
                ..._.omit(form, ['orderDate']), ...queryParams,
                fromDate,
                toDate
            }))
                .then(response => {
                    showErrorMessage(getFoodPartnerOrderHistoryNotificationData(response));
                });
        }
    }, [dispatch, open, form, queryParams, showErrorMessage]);

    const onOpen = () => {
        setQueryParams(initialQueryParams);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setTab(0);
        dispatch(invalidateFoodPartnerOrderHistory());
    };

    const onCancel = () => {
        setOrderIndex(0);
        setTab(0);
    };

    const onChangeDateFrom = (fromDate) => {
        setTab(0);
        setQueryParams(prevState => ({ ...prevState, fromDate }));
    };

    const onChangeDateTo = (toDate) => {
        setTab(0);
        setQueryParams(prevState => ({ ...prevState, toDate }));
    };

    const onClickOrder = (index) => {
        setOrderIndex(index);
        setTab(1);
    };

    const handleCopy = () => {
        if (form?.locationDivision) {
            onCopyHistoricalOrder(
                getFoodPartnerOrderHistory?.data?.content?.[orderIndex]?.mainWarehousePositions,
                getFoodPartnerOrderHistory?.data?.content?.[orderIndex]?.podPositions
            );
        } else {
            onCopyHistoricalOrder(
                getFoodPartnerOrderHistory?.data?.content?.[orderIndex]?.positions
            );
        }
        onClose();
    };

    const renderView = () => {
        if (getFoodPartnerOrderHistory?.loading) {
            return (
                <Box mt={ 6 } width="100%">
                    <LinearProgress/>
                </Box>
            );
        }

        if (getFoodPartnerOrderHistory?.data) {
            if (tab === 0) {
                return (
                    getFoodPartnerOrderHistory?.data?.content &&
                    <Box sx={ { width: '100%', mt: 3 } }>
                        <FoodPartnerOrderHistory onClickOrder={ onClickOrder }/>
                        <Box mt={ 2 }>
                            <PaginationControls
                                currentPage={ getFoodPartnerOrderHistory?.data?.pageable?.pageNumber }
                                totalPages={ getFoodPartnerOrderHistory?.data?.totalPages }
                                setPage={ page => setQueryParams(prevState => ({ ...prevState, page })) }
                            />
                        </Box>
                    </Box>
                );
            } else {
                return (
                    <Box sx={ { width: '100%', mt: 3 } }>
                        <FoodPartnerOrderHistoryDetails
                            orderIndex={ orderIndex }
                            locationDivision={ form?.locationDivision }/>
                    </Box>
                );
            }
        }

        return <></>;
    };

    return (
        <>
            <Button onClick={ onOpen } cursor="pointer">
                Wczytaj dane z historycznego zamówienia
            </Button>

            <Dialog
                open={ open }
                onClose={ onClose }
                fullWidth
                maxWidth="md"
                PaperProps={ { sx: { minHeight: 600, maxHeight: 800, borderRadius: 2 } } }
            >
                <DialogTitle sx={ { pb: 1 } }>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">
                                { tab === 0 ? "Historia zamówień" : "Szczegóły historycznego zamówienia" }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                { tab === 0 ? "Wybierz zamówienie z listy, aby podejrzeć jego szczegóły." : "Zweryfikuj pozycje przed załadowaniem ich do formularza." }
                            </Typography>
                        </Box>

                        { tab === 0 && (
                            <Stack direction="row" spacing={ 2 }>
                                <DatePicker
                                    displayWeekNumber
                                    label="Zamówienia od"
                                    onChange={ newDate => newDate?.isValid() && onChangeDateFrom(newDate) }
                                    slotProps={ { textField: { size: 'small', InputLabelProps: { shrink: true } } } }
                                    sx={ { width: 180 } }
                                />
                                <DatePicker
                                    displayWeekNumber
                                    label="Zamówienia do"
                                    onChange={ newDate => newDate?.isValid() && onChangeDateTo(newDate) }
                                    slotProps={ { textField: { size: 'small', InputLabelProps: { shrink: true } } } }
                                    sx={ { width: 180 } }
                                />
                            </Stack>
                        ) }
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    { renderView() }
                </DialogContent>

                <DialogActions sx={ { px: 3, py: 2 } }>
                    <Stack direction="row" spacing={ 2 } width="100%" justifyContent="space-between">
                        <Button onClick={ onClose } variant="outlined">Zamknij</Button>

                        { tab === 1 && (
                            <Stack direction="row" spacing={ 2 }>
                                <Button onClick={ onCancel } variant="outlined">Wróć do listy</Button>
                                <Button onClick={ handleCopy }>Załaduj pozycje</Button>
                            </Stack>
                        ) }
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CopyHistoricalDialog;